import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import MetricCard from './components/MetricCard';
import RevenueChart from './components/RevenueChart';
import DepartmentPerformance from './components/DepartmentPerformance';
import AlertsPanel from './components/AlertsPanel';
import QuickActions from './components/QuickActions';
import DateRangeSelector from './components/DateRangeSelector';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ExecutiveDashboard = () => {
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState('30d');

  // Mock data for metrics
  const metricsData = [
    {
      id: 'revenue',
      title: 'Total Revenue',
      value: '$2.4M',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'DollarSign',
      trend: [45, 52, 38, 67, 73, 89, 95, 78, 85, 92, 88, 94]
    },
    {
      id: 'orders',
      title: 'Active Orders',
      value: '1,247',
      change: '+8.2%',
      changeType: 'positive',
      icon: 'ShoppingCart',
      trend: [32, 45, 67, 54, 78, 65, 89, 76, 82, 91, 87, 93]
    },
    {
      id: 'customers',
      title: 'New Customers',
      value: '342',
      change: '-2.1%',
      changeType: 'negative',
      icon: 'Users',
      trend: [78, 65, 89, 76, 45, 52, 38, 67, 73, 82, 91, 87]
    },
    {
      id: 'efficiency',
      title: 'Operational Efficiency',
      value: '94.2%',
      change: '+5.7%',
      changeType: 'positive',
      icon: 'TrendingUp',
      trend: [67, 73, 89, 95, 78, 85, 92, 88, 94, 89, 91, 96]
    }
  ];

  // Mock data for revenue chart
  const revenueData = [
    { month: 'Jan', revenue: 185000 },
    { month: 'Feb', revenue: 192000 },
    { month: 'Mar', revenue: 178000 },
    { month: 'Apr', revenue: 205000 },
    { month: 'May', revenue: 218000 },
    { month: 'Jun', revenue: 234000 },
    { month: 'Jul', revenue: 241000 },
    { month: 'Aug', revenue: 228000 },
    { month: 'Sep', revenue: 245000 },
    { month: 'Oct', revenue: 252000 },
    { month: 'Nov', revenue: 238000 },
    { month: 'Dec', revenue: 267000 }
  ];

  // Mock data for department performance
  const departmentData = [
    { department: 'Sales', performance: 94 },
    { department: 'Marketing', performance: 87 },
    { department: 'Operations', performance: 91 },
    { department: 'HR', performance: 83 },
    { department: 'Finance', performance: 96 },
    { department: 'IT', performance: 89 },
    { department: 'Customer Service', performance: 92 },
    { department: 'R&D', performance: 85 }
  ];

  // Mock data for alerts
  const alertsData = [
    {
      id: 1,
      category: 'alert',
      type: 'critical',
      title: 'System Performance Alert',
      message: 'Database response time has exceeded threshold limits. Immediate attention required.',
      timestamp: new Date(Date.now() - 15 * 60 * 1000)
    },
    {
      id: 2,
      category: 'approval',
      type: 'warning',
      title: 'Budget Approval Required',
      message: 'Marketing department budget increase request pending executive approval.',
      timestamp: new Date(Date.now() - 45 * 60 * 1000)
    },
    {
      id: 3,
      category: 'notification',
      type: 'info',
      title: 'Monthly Report Generated',
      message: 'October 2025 executive summary report is now available for review.',
      timestamp: new Date(Date.now() - 2 * 60 * 60 * 1000)
    },
    {
      id: 4,
      category: 'approval',
      type: 'warning',
      title: 'New Hire Approval',
      message: 'Senior Developer position candidate requires final executive approval.',
      timestamp: new Date(Date.now() - 3 * 60 * 60 * 1000)
    },
    {
      id: 5,
      category: 'alert',
      type: 'warning',
      title: 'Inventory Low Stock',
      message: 'Multiple products are approaching minimum stock levels.',
      timestamp: new Date(Date.now() - 4 * 60 * 60 * 1000)
    },
    {
      id: 6,
      category: 'notification',
      type: 'info',
      title: 'System Maintenance Scheduled',
      message: 'Planned maintenance window scheduled for this weekend.',
      timestamp: new Date(Date.now() - 6 * 60 * 60 * 1000)
    }
  ];

  const handleSidebarToggle = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const handleMetricClick = (metricId) => {
    console.log(`Metric clicked: ${metricId}`);
    // Navigate to detailed view or open modal
  };

  const handleDateRangeChange = (range) => {
    setSelectedDateRange(range?.range);
    console.log('Date range changed:', range);
    // Update dashboard data based on selected range
  };

  const handleExportDashboard = () => {
    console.log('Exporting dashboard data...');
    // Implement export functionality
  };

  const handleCustomizeDashboard = () => {
    console.log('Opening dashboard customization...');
    // Open customization modal
  };

  useEffect(() => {
    // Simulate real-time data updates
    const interval = setInterval(() => {
      console.log('Updating dashboard data...');
      // Update metrics and charts with new data
    }, 30000); // Update every 30 seconds

    return () => clearInterval(interval);
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={handleSidebarToggle} />
      <Header sidebarCollapsed={sidebarCollapsed} />
      <main 
        className={`transition-smooth pt-16 ${
          sidebarCollapsed ? 'lg:pl-16' : 'lg:pl-64'
        }`}
      >
        <div className="p-6 space-y-6">
          {/* Header Section */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">
            <div>
              <Breadcrumb />
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-primary rounded-lg flex items-center justify-center">
                  <Icon name="BarChart3" size={20} color="white" />
                </div>
                <div>
                  <h1 className="text-2xl font-bold text-foreground">Executive Dashboard</h1>
                  <p className="text-muted-foreground">Strategic overview and key performance indicators</p>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-3">
              <DateRangeSelector onRangeChange={handleDateRangeChange} />
              <Button variant="outline" iconName="Download" onClick={handleExportDashboard}>
                Export
              </Button>
              <Button variant="default" iconName="Settings" onClick={handleCustomizeDashboard}>
                Customize
              </Button>
            </div>
          </div>

          {/* Key Metrics Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
            {metricsData?.map((metric) => (
              <MetricCard
                key={metric?.id}
                title={metric?.title}
                value={metric?.value}
                change={metric?.change}
                changeType={metric?.changeType}
                icon={metric?.icon}
                trend={metric?.trend}
                onClick={() => handleMetricClick(metric?.id)}
              />
            ))}
          </div>

          {/* Charts Section */}
          <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
            <div className="xl:col-span-2">
              <RevenueChart data={revenueData} />
            </div>
            <div>
              <DepartmentPerformance data={departmentData} />
            </div>
          </div>

          {/* Bottom Section */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2">
              <AlertsPanel alerts={alertsData} />
            </div>
            <div>
              <QuickActions />
            </div>
          </div>

          {/* Real-time Status Indicator */}
          <div className="fixed bottom-6 right-6 z-50">
            <div className="bg-card border border-border rounded-full px-4 py-2 card-shadow flex items-center space-x-2">
              <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
              <span className="text-xs text-muted-foreground">Live Data</span>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default ExecutiveDashboard;